import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import krookieJournalIcon from "../../../../images/krookie-journal.svg";
import Button from "../../../ui-kit/button/button";
import TableColumn from "../../../ui-kit/data-table/table-column/table-column";
import TableContainer from "../../../ui-kit/data-table/table/table-container";
import styles from "./reports-journal.module.scss";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import {
  addSelected,
  clearRevisionHistory,
  deleteAllSelected,
  deleteSelected,
  fetchReports,
  fetchRevisionHistory,
  ReportRow,
  setData,
} from "../../../../services/reducers/reports-journal";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import RowMenu from "../../../ui-kit/data-table/row-menu/row-menu";
import { Roles } from "../../../../types/roles";
import { useNavigate, useSearchParams } from "react-router-dom";
import RevisionHistoryModal from "./revision-history-modal/revision-history-modal";
import CreateQrCodeModal from "./create-qr-code-modal/create-qr-code-modal";
import { getReportDocument } from "../../../../services/api";
import DeleteReportModal from "./delete-report-modal/delete-report-modal";
import ChangeStatusReportModal from "./change-status-report-modal/change-status-report-modal";
import StatusCell from "../../../../shared/status-cell-render/status-cell";

const ReportsJournal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [pageSize, setPageSize] = useState<number>(0);
  const [hasNavigatedToSelection, setHasNavigatedToSelection] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    fullData,
    data,
    totalDataSize,
    selected,
    isLoading,
    revisionHistory,
  } = useAppSelector((state) => state.reportsJournal);
  const userRoles = useAppSelector((state) => state.user.user?.roles);
  const [isVisibleModalHistory, setVisibleModalHistory] =
    useState<boolean>(false);
  const [isVisibleModalCreateQrCode, setVisibleModalCreateQrCode] =
    useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<ReportRow | null>(
    null,
  );
  const [isVisibleDeleteReportModal, setVisibleDeleteReportModal] =
    useState<boolean>(false);
  const [isVisibleChangeStatusReportModal, setVisibleChangeStatusReportModal] =
    useState<boolean>(false);

  const isChangeStatusButtonDisabled = userRoles?.includes(Roles.StudyCreator);

  useEffect(() => {
    if (!fullData.length) {
      dispatch(fetchReports());
    }
  }, [fullData.length]);

  const reportMenuItems = (rowData: ReportRow) => [
    {
      label: t("reports-journal.menu.downloadPdf"),
      onClick: () => handleDownload(rowData.id, "pdf"),
    },
    {
      label: t("reports-journal.menu.downloadWord"),
      onClick: () => handleDownload(rowData.id, "word"),
    },
    {
      label: t("reports-journal.menu.seeRevisionHistory"),
      onClick: () => handleOpenRevisionHistoryModal(rowData),
    },
    {
      label: t("reports-journal.menu.generateQr"),
      onClick: () => handleOpenModalGenerateQrCode(rowData),
    },
    {
      label: t("reports-journal.menu.changeStatus"),
      onClick: () => handleOpenChangeStatusReportModal(rowData),
      disabled: !isChangeStatusButtonDisabled,
    },
    {
      label: t("reports-journal.menu.deleteReport"),
      onClick: () => handleOpenModalDeleteReport(rowData),
    },
  ];

  const handleDownload = async (reportId: number, type: "pdf" | "word") => {
    try {
      await getReportDocument(reportId, type);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenRevisionHistoryModal = (rowData: ReportRow) => {
    dispatch(fetchRevisionHistory(rowData.id));
    setVisibleModalHistory(true);
  };

  useEffect(() => {
    if (
      isLoading ||
      !fullData.length ||
      !pageSize ||
      !selected.length ||
      hasNavigatedToSelection
    )
      return;

    const maxSelectedId = Math.max(...selected);
    const index = fullData.findIndex((item) => item.id === maxSelectedId);

    if (index === -1) {
      return;
    }

    const page = Math.floor(index / pageSize) + 1;

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", String(page));
      return params;
    });

    setHasNavigatedToSelection(true);
  }, [isLoading, fullData, pageSize, selected, hasNavigatedToSelection]);

  useEffect(() => {
    if (isLoading) return;

    const sel = searchParams.get("ids");
    if (sel) {
      const ids = sel
        .split(",")
        .map((str) => parseInt(str, 10))
        .filter((id) => !selected.includes(id));
      if (ids.length) {
        dispatch(addSelected(ids));
      }
    }
  }, [isLoading]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (selected.length) {
          params.set("ids", selected.join(","));
        } else {
          params.delete("ids");
        }
        return params;
      },
      { replace: true },
    );
  }, [selected]);

  const handleAddSelected = (ids: number[]) => {
    dispatch(addSelected(ids));
  };

  const handleDeleteSelected = (ids: number[]) => {
    dispatch(deleteSelected(ids));
  };

  const handleDeleteAllSelected = () => {
    dispatch(deleteAllSelected());
  };

  const handleOpenModalGenerateQrCode = (rowData: ReportRow) => {
    setSelectedRowData(rowData);
    setVisibleModalCreateQrCode(true);
  };

  const handleOpenModalDeleteReport = (rowData: ReportRow) => {
    setSelectedRowData(rowData);
    setVisibleDeleteReportModal(true);
  };

  const handleOpenChangeStatusReportModal = (rowData: ReportRow) => {
    setSelectedRowData(rowData);
    setVisibleChangeStatusReportModal(true);
  };

  return (
    <>
      <TableContainer
        idField="id"
        tableName={t("reports-journal.tableName")}
        tableIcon={krookieJournalIcon}
        dataSource={fullData}
        rowHeight={66}
        dateTemplate="MM/DD/YYYY hh:mm pm"
        debounceMs={1000}
        data={data}
        totalDataSize={totalDataSize}
        selected={selected}
        setData={(payload) => dispatch(setData(payload))}
        addSelected={handleAddSelected}
        deleteSelected={handleDeleteSelected}
        deleteAllSelected={handleDeleteAllSelected}
        placeholderSearch={t("reports-journal.searchPlaceholder")}
        substringFields={["cityName", "status", "userName"]}
        headerContent={() => (
          <div className={styles.footerBtn}>
            <Button
              onClick={() => navigate("/reportMaster")}
              disabled={!userRoles?.includes(Roles.StudyCreator)}
              type="withIcon"
              className={styles.btn}
            >
              <span className={styles.icon} />{" "}
              {t("reports-journal.createStudyButton")}
            </Button>
          </div>
        )}
        visibleFilter={false}
        renderRowMenu={(rowData) => (
          <RowMenu items={reportMenuItems(rowData as ReportRow)} />
        )}
        secondLineOpacity={true}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
      >
        <TableColumn
          dataField="cityName"
          caption={t("reports-journal.columns.name")}
          style={{ width: "236px", textAlign: "start" }}
        />
        <TableColumn
          dataField="governorate"
          caption={t("reports-journal.columns.governorates")}
          style={{ width: "236px", textAlign: "start" }}
        />
        <TableColumn
          dataField="status"
          caption={t("reports-journal.columns.status")}
          renderCell={(value: string) => <StatusCell status={value} />}
          style={{ width: "236px", textAlign: "start" }}
        />
        <TableColumn
          dataField="userName"
          caption={t("reports-journal.columns.drafter")}
          style={{ width: "236px", textAlign: "start" }}
        />
        <TableColumn
          dataField="modificationDateAndCreationDate"
          caption={t("reports-journal.columns.modificationDate")}
          style={{ width: "236px", textAlign: "start" }}
        />
      </TableContainer>
      {isVisibleModalHistory && revisionHistory && (
        <RevisionHistoryModal
          revisionHistory={revisionHistory}
          onClose={() => {
            dispatch(clearRevisionHistory());
            setVisibleModalHistory(false);
          }}
        />
      )}
      {isVisibleModalCreateQrCode && selectedRowData && (
        <CreateQrCodeModal
          onClose={() => {
            setSelectedRowData(null);
            setVisibleModalCreateQrCode(false);
          }}
          cityName={selectedRowData.name}
          reportId={selectedRowData.id}
        />
      )}

      {isVisibleDeleteReportModal && selectedRowData && (
        <DeleteReportModal
          onClose={() => {
            setSelectedRowData(null);
            setVisibleDeleteReportModal(false);
          }}
          reportId={selectedRowData.id}
          cityName={selectedRowData.cityName}
        />
      )}

      {isVisibleChangeStatusReportModal && selectedRowData && (
        <ChangeStatusReportModal
          selectedRowData={selectedRowData}
          onClose={() => {
            setSelectedRowData(null);
            setVisibleChangeStatusReportModal(false);
          }}
        />
      )}
    </>
  );
};

export default ReportsJournal;
